import type { FormInstance } from "rc-field-form";
import type { Rule, RuleObject } from "rc-field-form/es/interface";
import * as React from "react";
import { KuikaFormField } from "../../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { DraftRowBlankTest, isBlankText, withoutBlankRows } from "../../shared/kuika-edit-grid/draft-rows";
import {
  editGridNavId,
  focusEditGridCell,
  IKuikaEditGridColumn,
  KuikaEditGrid,
  KuikaEditGridCell,
  KuikaEditGridRemove,
  KuikaEditGridRow
} from "../../shared/kuika-edit-grid/kuika-edit-grid";
import {
  IEditGridListApi,
  IEditGridListRow,
  KuikaEditGridFormList
} from "../../shared/kuika-edit-grid/kuika-edit-grid-form-list";

export type ApiEndpointDataType = "string" | "number" | "integer" | "boolean" | "array" | "object";
export type ApiEndpointLocation = "Path" | "Header" | "QueryString";

/** One endpoint parameter as the form edits it. */
export interface IRestEndpointParameterRow {
  key: string;
  value: string;
  location: ApiEndpointLocation;
  type: ApiEndpointDataType;
}

/** Where the rows live in the endpoint form's store. */
export const ENDPOINT_PARAMETERS_PATH: string[] = ["parameters"];

const TEST_ID_PREFIX: string = "restApiEndpoint";

const DEFAULT_LOCATION: ApiEndpointLocation = "QueryString";
const DEFAULT_TYPE: ApiEndpointDataType = "string";

const LOCATION_OPTIONS: KuikaSelectboxOption[] = [
  { value: "QueryString", label: "queryString" },
  { value: "Path", label: "path" },
  { value: "Header", label: "header" }
];

const TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: "string", label: "string" },
  { value: "number", label: "number" },
  { value: "integer", label: "integer" },
  { value: "boolean", label: "boolean" },
  { value: "object", label: "object" },
  { value: "array", label: "array" }
];

const REQUIRED_MESSAGE: string = "This field is required";
const KEY_PATTERN: RegExp = /^[A-Za-z_-][A-Za-z0-9_-]*$/;

const COLUMNS: IKuikaEditGridColumn[] = [
  { id: "key", header: "Key", track: "minmax(0, 1.2fr)", headerTestId: `${TEST_ID_PREFIX}ParameterKeyText` },
  { id: "value", header: "Value", track: "minmax(0, 1.2fr)", headerTestId: `${TEST_ID_PREFIX}ParameterValueText` },
  { id: "location", header: "Location", track: "132px", headerTestId: `${TEST_ID_PREFIX}ParameterLocationText` },
  { id: "type", header: "Type", track: "116px", headerTestId: `${TEST_ID_PREFIX}ParameterTypeText` },
  { id: "remove", track: "52px" }
];

/** The grid's trailing row — where the next parameter is typed — with the old ADD's defaults. */
export const blankEndpointParameter = (): IRestEndpointParameterRow => ({
  key: "",
  value: "",
  location: DEFAULT_LOCATION,
  type: DEFAULT_TYPE
});

/**
 * A row nothing has been typed into or chosen on: the draft row, or a row cleared back to it. A
 * location or a type picked on the draft counts — that is the start of a parameter too.
 */
export const isBlankEndpointParameter: DraftRowBlankTest<IRestEndpointParameterRow> = (
  row: IRestEndpointParameterRow | undefined
): boolean =>
  isBlankText(row?.key) &&
  isBlankText(row?.value) &&
  (!row?.location || row.location === DEFAULT_LOCATION) &&
  (!row?.type || row.type === DEFAULT_TYPE);

/** The parameters that ARE parameters — what the endpoint saves, runs, hashes and checks its path against. */
export const withoutBlankEndpointParameters = (
  rows: IRestEndpointParameterRow[] | undefined
): IRestEndpointParameterRow[] => withoutBlankRows(rows, isBlankEndpointParameter);

/**
 * Why the key at `index` is refused, or null. A blank row is never refused: it is where the next
 * parameter goes, and a save drops it. Otherwise the endpoint's rules: required, a valid name,
 * not repeated.
 */
export const endpointParameterKeyProblem = (
  rows: IRestEndpointParameterRow[] | undefined,
  index: number
): string | null => {
  const row: IRestEndpointParameterRow | undefined = rows?.[index];
  if (isBlankEndpointParameter(row)) return null;

  const key: string = row?.key ?? "";
  if (!key) return REQUIRED_MESSAGE;
  if (!KEY_PATTERN.test(key)) return "Please enter a valid parameter key.";

  const isUsed: boolean = (rows ?? []).some(
    (other: IRestEndpointParameterRow, otherIndex: number): boolean => otherIndex !== index && other?.key === key
  );
  return isUsed ? "This key value is used before." : null;
};

/** Value, location and type are required on every row but a blank one. */
const requiredUnlessBlank = (index: number): Rule => {
  const rule = (form: FormInstance): RuleObject => ({
    validator: async (_rule: RuleObject, value: unknown): Promise<void> => {
      const rows: IRestEndpointParameterRow[] | undefined = form.getFieldValue(ENDPOINT_PARAMETERS_PATH);
      if (isBlankEndpointParameter(rows?.[index])) return;
      if (value === undefined || value === null || value === "") throw new Error(REQUIRED_MESSAGE);
    }
  });
  return rule;
};

const keyRule = (index: number): Rule => {
  const rule = (form: FormInstance): RuleObject => ({
    validator: async (): Promise<void> => {
      const problem: string | null = endpointParameterKeyProblem(form.getFieldValue(ENDPOINT_PARAMETERS_PATH), index);
      if (problem) throw new Error(problem);
    }
  });
  return rule;
};

/**
 * The endpoint's parameters: what each is called, its value, where it rides and its type. The last
 * row is always the blank one the next parameter is written in — there is no ADD button — and
 * Enter walks key → value → the next row's key. A `Path` parameter is also checked against the
 * path itself when the request validates.
 */
export const RestEndpointParametersTable = (): React.ReactElement => (
  <div className="kuika-form-section__wide">
    <KuikaEditGridFormList<IRestEndpointParameterRow>
      name={ENDPOINT_PARAMETERS_PATH}
      isBlank={isBlankEndpointParameter}
      makeBlank={blankEndpointParameter}
    >
      {(rows: IEditGridListRow[], { settle, remove }: IEditGridListApi): React.ReactNode => (
        <KuikaEditGrid columns={COLUMNS} label="Parameters" testId={`${TEST_ID_PREFIX}ParametersGrid`}>
          {rows.map((row: IEditGridListRow): JSX.Element => {
            const next: IEditGridListRow | undefined = rows[row.index + 1];
            const draftTestId = (suffix: string): string | undefined =>
              row.isDraft ? `${TEST_ID_PREFIX}ParameterDraft${suffix}` : undefined;

            return (
              <KuikaEditGridRow key={row.key} testId={draftTestId("Row")}>
                <KuikaEditGridCell flush>
                  <KuikaFormField name={[row.name, "key"]} rules={[keyRule(row.index)]}>
                    <KuikaInput
                      size="md"
                      borderless
                      placeholder="Enter key.."
                      autoComplete="off"
                      data-testid={draftTestId("KeyInput")}
                      data-edit-grid-nav={editGridNavId(row.key, "key")}
                      onChange={settle}
                      onPressEnter={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                        event.preventDefault();
                        focusEditGridCell(event.currentTarget, editGridNavId(row.key, "value"));
                      }}
                    />
                  </KuikaFormField>
                </KuikaEditGridCell>
                <KuikaEditGridCell flush>
                  <KuikaFormField name={[row.name, "value"]} rules={[requiredUnlessBlank(row.index)]}>
                    <KuikaInput
                      size="md"
                      borderless
                      placeholder="Enter value.."
                      autoComplete="off"
                      data-testid={draftTestId("ValueInput")}
                      data-edit-grid-nav={editGridNavId(row.key, "value")}
                      onChange={settle}
                      onPressEnter={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                        event.preventDefault();
                        if (next) focusEditGridCell(event.currentTarget, editGridNavId(next.key, "key"));
                      }}
                    />
                  </KuikaFormField>
                </KuikaEditGridCell>
                <KuikaEditGridCell flush>
                  <KuikaFormField name={[row.name, "location"]} rules={[requiredUnlessBlank(row.index)]}>
                    <KuikaSelectbox
                      size="md"
                      className="kuika-edit-grid__select"
                      options={LOCATION_OPTIONS}
                      data-testid={draftTestId("LocationSelect")}
                      onChange={settle}
                    />
                  </KuikaFormField>
                </KuikaEditGridCell>
                <KuikaEditGridCell flush>
                  <KuikaFormField name={[row.name, "type"]} rules={[requiredUnlessBlank(row.index)]}>
                    <KuikaSelectbox
                      size="md"
                      className="kuika-edit-grid__select"
                      options={TYPE_OPTIONS}
                      data-testid={draftTestId("TypeSelect")}
                      onChange={settle}
                    />
                  </KuikaFormField>
                </KuikaEditGridCell>
                <KuikaEditGridCell center>
                  {/* The draft is where the next parameter goes: there is nothing there to remove. */}
                  {!row.isDraft && (
                    <KuikaEditGridRemove label="Remove parameter" onClick={(): void => remove(row.index)} />
                  )}
                </KuikaEditGridCell>
              </KuikaEditGridRow>
            );
          })}
        </KuikaEditGrid>
      )}
    </KuikaEditGridFormList>
  </div>
);
